import { usePortfolioData } from '@/hooks/use-portfolio-data';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import * as Icons from 'lucide-react';

export function Footer() {
  const { data: portfolioData, isLoading } = usePortfolioData();
  const currentYear = new Date().getFullYear();

  const DynamicIcon = ({ name }: { name: string }) => {
    const LucideIcon = (Icons as any)[name] || Icons.Circle;
    return <LucideIcon className="h-5 w-5" />;
  };

  if (isLoading) {
    return (
      <footer className="bg-dark-900 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-4 w-60 mt-2" />
            </div>
            <div className="flex space-x-4">
              {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} className="h-10 w-10 rounded-full" />
              ))}
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <Skeleton className="h-4 w-60" />
            <Skeleton className="h-8 w-40 mt-4 md:mt-0" />
          </div>
        </div>
      </footer>
    );
  }

  if (!portfolioData) return null;

  return (
    <footer className="bg-slate-900 text-white py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="mb-6 md:mb-0">
            <a href="#hero" className="text-xl font-bold flex items-center justify-center md:justify-start">
              <span className="text-primary">&lt;</span>
              <span className="bg-gradient-to-r from-primary to-violet-500 text-transparent bg-clip-text">DevPortfolio</span>
              <span className="text-primary">/&gt;</span>
            </a>
            <p className="text-gray-400 mt-2 text-center md:text-left">{portfolioData.footerTagline}</p>
          </div>
          
          <div className="flex space-x-4">
            {portfolioData.socialLinks.map((link) => (
              <a 
                key={link.url}
                href={link.url} 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label={link.name}
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-primary transition-colors"
              >
                <DynamicIcon name={link.icon} />
              </a>
            ))}
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm">
            © {currentYear} {portfolioData.name}. All rights reserved.
          </p>
          
          <div className="mt-4 md:mt-0">
            <Button asChild variant="link" className="text-gray-400 hover:text-primary p-0 h-auto font-normal">
              <a 
                href={portfolioData.resumeUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center"
              >
                <Download className="mr-2 h-4 w-4" /> Download Resume
              </a>
            </Button>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
